import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Divider,
    MenuItem,
    Typography,
} from "@mui/material";

import { SecondaryTab, StyledTabContainer } from "../../../Components/CustomTab";
import CloseButton from "../../../Components/CloseButton";
import { StyledDialog, StyledTextField } from "../../../Components/EditVehicleDetails/EditDialogShared";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import {
    CancelFormActionButton,
    PrimaryFormLoadingActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { ThemeProperties } from "../../../Theme/ThemeProperties";

import WorkOrderDetailView from "./WorkOrderDetailView";
import ReviewWorkOrderDialog from "./ReviewWorkOrderDialog";
import useWorkOrders from "./hooks/useWorkOrders";
import { useCurrentUserID } from "./hooks/useWorkOrderConfig";
import {
    HISTORY_STATUS_ALL,
    WORK_ORDER_STATUS,
    buildCSVColumnsPayload,
    canEditWorkOrder,
    canReviewWorkOrder,
    formatWorkOrderDateTime,
    historyStatusSlug,
    mapWorkOrderFiltersToParams,
    workOrderStatusText,
    workOrderStatusTheme,
} from "./workOrderHelpers";
import useConfiguredFilters from "../hooks/useConfiguredFilters";
import {
    deleteWorkOrder,
    getWorkOrderSearchSuggestions,
    workOrderListAction,
} from "../../../Api/WorkOrderApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { usePermissions } from "../../../Context/PermissionContext";
import { useToast } from "../../../Hooks/useToast";
import { getAvailableEmailDomains } from "../../../Helper/Organization/EmailDomains";
import { STATUS_PALETTE } from "../statusPalette";
import { FORM_TOKENS } from "./WorkOrderFormShared";
import { ASSET_MANAGEMENT_LABEL } from "../../../Static/ModuleLabels";

// The Work Orders queue: the maker-checker flow's front door.
//
// Two tabs. REQUESTS is what is waiting on a checker (status = pending);
// HISTORY is what has been decided. The counts on both come from
// /api/work-orders/counts, which honours every filter EXCEPT status -- so the
// badges say what the other tab holds rather than what the current one does.
//
// History carries a second, small selector, and it DEFAULTS TO ALL. The list
// endpoint's status is a CommaSeparatedStrList, so "approved,rejected" is one
// request the server pages and sorts itself -- no client-side merge of two
// paged responses, which would be wrong the moment either side has more than
// one page. Narrowing to one status stays available, with its own count, but a
// decided work order is never absent from a tab whose badge counted it.
//
// NO work order config is fetched here any more. The type DROPDOWN is a panel
// filter resolved from pageMeta, and the one thing the config was still read
// for -- whether to offer "+ New Work Order" -- moved to the module shell with
// the button itself, which is now floating and on screen from every tab.

// The one name for the action column, matching the six network tables and the
// platform helper at GenericDraggableTableFuncations.js:21. Named rather than
// inlined because it was previously repeated as a literal in five places, and
// renaming it meant finding all five.
const ACTION_COLUMN_TYPE = "action-menu";

const TAB_REQUESTS = "requests";
const TAB_HISTORY = "history";

// The TEXT is the server's statusLabel, read verbatim. Only the COLOUR is
// decided here, from status + reviewAction -- that is presentation, and an
// auto-approved row is amber rather than green because it is not a failure but
// it is not a signed-off approval either.
const StatusChip = ({ row }) => {
    const theme = workOrderStatusTheme(row);
    return (
        <Box
            component="span"
            data-testid={`work-order-status-${row?.id}`}
            sx={{
                display: "inline-block",
                px: 1.25,
                py: "3px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                color: theme.color,
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`,
            }}
        >
            {workOrderStatusText(row)}
        </Box>
    );
};

const WorkOrdersTabContent = ({ metaInfo }) => {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { openNotification } = useToast();
    const { currentUserID, isResolved: userResolved } = useCurrentUserID();

    const [activeTab, setActiveTab] = useState(TAB_REQUESTS);
    // BOTH decided statuses, not just approved. See HISTORY_STATUS_ALL.
    const [historyStatus, setHistoryStatus] = useState(HISTORY_STATUS_ALL);

    // THIS SPLIT IS THE STATUS FILTER. A panel filter must never duplicate it:
    // useWorkOrders spreads filterParams after its own status, so a declared
    // `status` would silently win and fill a tab labelled "Requests" with
    // approved rows.
    const status =
        activeTab === TAB_REQUESTS ? WORK_ORDER_STATUS.PENDING : historyStatus;

    const {
        records,
        total,
        counts,
        isLoading,
        errorMessage,
        page,
        limit,
        setPage,
        applySearch,
        applySort,
        applyFilterParams,
        requestArgs,
        refetch,
    } = useWorkOrders(status);

    // --- narrowing the list ------------------------------------------------
    // The work order TYPE is a panel filter, declared in pageMeta
    // (metaInfo.filterConfigurations.workOrdersTable.filters) and resolved by
    // the SHARED fetchFiltersDataObject. It used to be a header dropdown; that
    // control is gone, because two controls writing one field disagree the
    // moment either is touched. An org with no block declared gets {} and no
    // panel is rendered at all.
    const { filters } = useConfiguredFilters(metaInfo, "workOrdersTable");

    // The panel's own selection. The REQUEST state is the hook's single query
    // object; this is what the panel draws its ticks from.
    const [selectedFilters, setSelectedFilters] = useState({});

    // No effect syncing the two. An effect would commit the selection in one
    // render and the request in the next, which is the shape the hooks' single
    // state object exists to prevent.
    const applyNarrowing = useCallback(
        (nextFilters) => {
            // What the panel shows and what the request carries are the same
            // object: the whole selection is stored and the whole selection is
            // mapped, so a tick cannot be silently dropped on the way out.
            const selection = nextFilters || {};
            setSelectedFilters(selection);
            applyFilterParams(mapWorkOrderFiltersToParams(selection));
        },
        [applyFilterParams]
    );

    // There is no row selection on this table (see the note on the table
    // below), so unlike the Assets tab there is nothing to clear here.
    const applyFilters = useCallback(
        (next) => applyNarrowing(next),
        [applyNarrowing]
    );

    // --- dialogs -----------------------------------------------------------
    const [detailTarget, setDetailTarget] = useState(null);
    const [reviewTarget, setReviewTarget] = useState(null);
    const [withdrawTarget, setWithdrawTarget] = useState(null);
    const [withdrawing, setWithdrawing] = useState(false);

    const emailDomains = useMemo(() => getAvailableEmailDomains(metaInfo), [metaInfo]);

    const reviewGateFor = useCallback(
        (row) =>
            canReviewWorkOrder({
                row,
                // Until the id has resolved the gate stays CLOSED. A Review
                // button that appears and then disappears once the identity
                // lands is worse than one that appears a moment late.
                currentUserID: userResolved ? currentUserID : undefined,
                hasPermission,
            }),
        [currentUserID, userResolved, hasPermission]
    );

    const canEditRow = useCallback(
        (row) =>
            userResolved &&
            canEditWorkOrder({ row, currentUserID, hasPermission }),
        [currentUserID, userResolved, hasPermission]
    );

    // The form is a PAGE, not a dialog -- a request can carry hundreds of
    // serials and the per-serial report that comes back on failure has to
    // render beside the form that produced it. So this navigates.
    //
    // RAISING one is not here at all any more. It is a floating button on the
    // module shell, on screen from every tab, gated by the same
    // canRaiseAnyWorkOrder predicate -- a second control in this toolbar doing
    // the same thing in the same viewport was noise.
    //
    // No record travels with the navigation. The edit page refetches the DETAIL
    // by id, because the list row carries only the header: handing it over
    // would show an edit screen with no movements on it and then replace the
    // whole set on save -- a silent wipe. Refetching also means the URL alone
    // is enough, so an edit link can be reopened or shared.
    const openEdit = useCallback(
        (row) => {
            if (!row?.id) return;
            navigate(`/asset-management/work-orders/${row.id}/edit`);
        },
        [navigate]
    );


    const handleWithdraw = useCallback(async () => {
        if (!withdrawTarget) return;
        setWithdrawing(true);
        try {
            const res = await deleteWorkOrder(withdrawTarget.id);
            if (res?.responseStatus?.code === CODE.SUCCESS) {
                openNotification({
                    text: res?.responseStatus?.msg || "Work order withdrawn",
                    icon: "toast-success",
                });
                setWithdrawTarget(null);
                setDetailTarget(null);
                refetch(records.length);
                return;
            }
            openNotification({
                text: res?.responseStatus?.msg || "Could not withdraw the work order",
                icon: "toast-error",
            });
        } finally {
            setWithdrawing(false);
        }
    }, [withdrawTarget, openNotification, refetch, records.length]);

    // --- columns -----------------------------------------------------------
    //
    // status-chip, date-time and actions are NOT types TableColumnValueRenderer
    // knows; its switch returns null for an unknown one, so a column configured
    // with them renders an empty cell unless a customRenderer is supplied here.
    // customRenderers is consulted BEFORE the switch, which is what makes this
    // the extension point rather than a fork of the shared renderer.
    //
    // Signature is POSITIONAL: TableColumnValueRenderer calls
    // customRenderers[type](record, config). Destructuring `record` off the
    // record object itself yields undefined.
    const customRenderers = useMemo(
        () => ({
            "status-chip": (record) => <StatusChip row={record} />,
            "date-time": (record, config) => (
                <Typography variant="fw400s14lh24c3D4B5A">
                    {formatWorkOrderDateTime(
                        record?.[config?.dataIndex],
                        config?.defaultValue || "-"
                    )}
                </Typography>
            ),
            "action-menu": (record) => {
                const gate = reviewGateFor(record);
                const editable = canEditRow(record);
                return (
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        <Button
                            size="small"
                            data-testid={`work-order-view-${record?.id}`}
                            onClick={() => setDetailTarget(record)}
                            sx={{
                                fontSize: 12,
                                minWidth: 0,
                                px: 1,
                                color: ThemeProperties.purple,
                                textTransform: "none",
                            }}
                        >
                            View
                        </Button>
                        {gate.allowed && (
                            <Button
                                size="small"
                                data-testid={`work-order-review-${record?.id}`}
                                onClick={() => setReviewTarget(record)}
                                sx={{
                                    fontSize: 12,
                                    minWidth: 0,
                                    px: 1,
                                    color: ThemeProperties.purple,
                                    textTransform: "none",
                                }}
                            >
                                Review
                            </Button>
                        )}
                        {editable && (
                            <Button
                                size="small"
                                data-testid={`work-order-edit-${record?.id}`}
                                onClick={() => openEdit(record)}
                                sx={{
                                    fontSize: 12,
                                    minWidth: 0,
                                    px: 1,
                                    color: ThemeProperties.purple,
                                    textTransform: "none",
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        {/* The maker is told WHY there is no Review button
                            beside their own request, rather than being left to
                            wonder where it went. The full sentence is on the
                            detail page; this is the short form that fits a
                            row. */}
                        {!gate.allowed && gate.reason && (
                            <Typography
                                data-testid={`work-order-review-blocked-${record?.id}`}
                                title={gate.reason}
                                sx={{ fontSize: 11, color: STATUS_PALETTE.warning.fg, whiteSpace: "nowrap" }}
                            >
                                Yours to fix, not to approve
                            </Typography>
                        )}
                    </Box>
                );
            },
        }),
        [reviewGateFor, canEditRow, openEdit]
    );

    const tableConfig = metaInfo?.table?.workOrdersTable || null;
    const columnsConfig = tableConfig?.columnsConfig || null;
    const columnOrdering = useMemo(
        () => tableConfig?.columnOrdering || [],
        [tableConfig]
    );

    const allColumns = useMemo(() => {
        if (!columnOrdering.length) return [];
        return getColumnsConfig({
            tableColumns: columnsConfig,
            tableColumnOrder: columnOrdering,
            shouldShowActionMenu: () => true,
            customRenderers,
        });
    }, [columnsConfig, columnOrdering, customRenderers]);

    // The actions column is never in the picker: it is not data, and hiding it
    // would take away View, Review and Edit with no way back.
    const pickableColumns = useMemo(
        () => allColumns.filter((c) => c.valueRenderingType !== ACTION_COLUMN_TYPE),
        [allColumns]
    );

    const defaultSelectedColumns = useMemo(
        () =>
            columnOrdering
                .filter(
                    (key) =>
                        columnsConfig?.[key] &&
                        columnsConfig[key].valueRenderingType !== ACTION_COLUMN_TYPE &&
                        columnsConfig[key].defaultVisible !== false
                )
                // dataIndex, NOT the config key: TableColumns is written
                // entirely against dataIndex, so a config whose key and
                // dataIndex differ would render every box unchecked.
                .map((key) => columnsConfig[key].dataIndex || key),
        [columnsConfig, columnOrdering]
    );

    const [selectedColumns, setSelectedColumns] = useState([]);

    useEffect(() => {
        setSelectedColumns(defaultSelectedColumns);
    }, [defaultSelectedColumns]);

    const columns = useMemo(() => {
        const visible = columnOrdering
            .map((key) => pickableColumns.find((c) => c.key === key))
            .filter((c) => c && selectedColumns.includes(c.dataIndex));
        const actions = allColumns.filter((c) => c.valueRenderingType === ACTION_COLUMN_TYPE);
        return [...visible, ...actions];
    }, [columnOrdering, selectedColumns, pickableColumns, allColumns]);

    // `title` is only a fallback where it is still a string: getColumnsConfig
    // replaces it with a render function, and a function in this list becomes
    // an empty CSV column rather than an error.
    const headerLabelOf = useCallback(
        (c) =>
            typeof c.headerLabel === "string"
                ? c.headerLabel
                : typeof c.title === "string"
                    ? c.title
                    : c.dataIndex,
        []
    );

    const [draggedOrder, setDraggedOrder] = useState(null);

    const onColumnOrderChange = useCallback(
        (cols) =>
            setDraggedOrder(
                (cols || [])
                    .filter((c) => c.valueRenderingType !== ACTION_COLUMN_TYPE)
                    .map(headerLabelOf)
            ),
        [headerLabelOf]
    );

    // Dropped whenever the visible set changes: a drag order captured before a
    // column was hidden names a column the file no longer has.
    useEffect(() => {
        setDraggedOrder(null);
    }, [selectedColumns]);

    const columnOrder = useMemo(
        () =>
            draggedOrder ||
            columns
                .filter((c) => c.valueRenderingType !== ACTION_COLUMN_TYPE)
                .map(headerLabelOf),
        [columns, draggedOrder, headerLabelOf]
    );

    const searchableFields = useMemo(
        () => tableConfig?.searchableFields || [],
        [tableConfig]
    );

    // GenericDraggableTable calls onSortChange(columnKey, "asc"|"desc"),
    // already normalised.
    const handleSort = useCallback(
        (key, order) => applySort(key || null, order || null),
        [applySort]
    );

    // --- export ------------------------------------------------------------
    //
    // Requests the SAME search/sort/filter arguments the list is showing, so
    // the file can never cover a different set of rows than the screen.
    //
    // The rows come back ALREADY renamed to header labels, with the dates
    // localised and statusLabel derived by the server, so NOTHING is
    // transformed here -- which is how the Data Transform Parity Rule is
    // satisfied on this page. There is no client formatter that could drift,
    // and the emailed attachment, which the server builds from these very rows
    // and which never passes through this file, says the same thing for free.
    const getCSVData = useCallback(async () => {
        const res = await workOrderListAction({
            actionType: "download-csv",
            columns: buildCSVColumnsPayload(columns),
            columnOrder,
            ...requestArgs,
        });
        if (res?.responseStatus?.code !== CODE.SUCCESS) {
            openNotification({
                text: res?.responseStatus?.msg || "Could not prepare the download",
                icon: "toast-error",
            });
            return [];
        }
        if (res?.data?.truncated) {
            openNotification({
                text: `Only the first ${res.data.limit} rows were exported. Narrow the filters for the rest.`,
                icon: "toast-error",
            });
        }
        return res?.data?.records || [];
    }, [columns, columnOrder, requestArgs, openNotification]);

    // The server builds the attachment from the same query the table ran; it is
    // NOT handed the downloaded rows.
    //
    // An earlier version awaited getCSVData() and passed the result as `rows`.
    // workOrderListAction does not destructure `rows`, so it fell into
    // ...filters and buildWorkOrderParams dropped it -- the await was a whole
    // wasted download-csv POST per send, which could also raise a truncation
    // toast in the middle of composing an email.
    const onSendEmail = useCallback(
        async ({ sendTo, sendCc, subject, body }) => {
            const res = await workOrderListAction({
                actionType: "mail",
                // The same visible columns, and no sentinel: for `mail` the
                // server writes the file itself, so anything extra in this list
                // becomes a literal column in the recipient's CSV.
                columns: buildCSVColumnsPayload(columns),
                columnOrder,
                sendTo,
                sendCc,
                subject,
                body,
                fileName: "work-orders",
                ...requestArgs,
            });
            if (res?.responseStatus?.code !== CODE.SUCCESS) {
                const message = res?.responseStatus?.msg || "Could not send the email";
                // Toasted AND thrown. The throw keeps the composer open so the
                // user does not lose what they typed; the toast is what they
                // actually see, because the composer swallows the rejection
                // into console.error.
                openNotification({ text: message, icon: "toast-error" });
                throw new Error(message);
            }
        },
        [columns, columnOrder, requestArgs, openNotification]
    );

    const getCSVFileName = useCallback(
        () =>
            `work-orders-${
                activeTab === TAB_REQUESTS
                    ? "requests"
                    : historyStatusSlug(historyStatus)
            }.csv`,
        [activeTab, historyStatus]
    );

    const historyCount = (counts.approved || 0) + (counts.rejected || 0);

    return (
        <>
            {/* No px here: the page body supplies the inset (CLAUDE.md, Page
                Layout Rule), so this row, the strip under it and the card
                below all line up on one edge. Adding it back would indent
                them by 48px. */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    borderBottom: `1px solid ${FORM_TOKENS.cardBorder}`,
                    mb: 1,
                }}
            >
                <StyledTabContainer
                    value={activeTab}
                    onChange={(_, tab) => setActiveTab(tab)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label="Work order queues"
                    sx={{ minHeight: 0, "& .MuiTab-root": { px: 3 } }}
                >
                    <SecondaryTab
                        value={TAB_REQUESTS}
                        label={`Requests (${counts.pending || 0})`}
                        data-testid="work-order-tab-requests"
                    />
                    <SecondaryTab
                        value={TAB_HISTORY}
                        label={`History (${historyCount})`}
                        data-testid="work-order-tab-history"
                    />
                </StyledTabContainer>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                    {activeTab === TAB_HISTORY && (
                        <StyledTextField
                            select
                            size="small"
                            value={historyStatus}
                            onChange={(e) => setHistoryStatus(e.target.value)}
                            sx={{ minWidth: 190 }}
                            inputProps={{ "data-testid": "work-order-history-status" }}
                        >
                            <MenuItem value={HISTORY_STATUS_ALL}>
                                All ({historyCount})
                            </MenuItem>
                            <MenuItem value={WORK_ORDER_STATUS.APPROVED}>
                                Approved ({counts.approved || 0})
                            </MenuItem>
                            <MenuItem value={WORK_ORDER_STATUS.REJECTED}>
                                Rejected ({counts.rejected || 0})
                            </MenuItem>
                        </StyledTextField>
                    )}

                    {/* Two header controls used to sit here and both are GONE.
                        The type selector became a panel filter
                        (workOrderTypeCode); keeping both would have been two
                        controls for one narrowing, with the header one still
                        reading "All types" while the panel filtered the list.
                        "Raised by me" was removed on the user's instruction --
                        the quick filters are being reconsidered as a set. The
                        createdByMe PARAM is untouched on both the request
                        mapper and the backend, so bringing it back is a control
                        and nothing else. */}
                </Box>
            </Box>

            <Box
                sx={{
                    // No mt. The tab strip above owns the gap below itself
                    // (its mb), so only one number decides the spacing.
                    "& .ant-table-column-sorter": { display: "none" },
                    "& .ant-table-column-has-sorters .ant-table-cell-content": {
                        cursor: "pointer",
                    },
                }}
            >
                <GenericDraggableTableView
                    // Remounted per status. TableSearch seeds its selected
                    // column from searchableFields[0] in useState ONCE, and it
                    // also holds the chips -- both of which should start clean
                    // when the queue being looked at changes.
                    key={status}
                    title={activeTab === TAB_REQUESTS ? "Requests" : "History"}
                    count={total}
                    // EXPLICIT false. TableHeaderAndActions defaults
                    // showPrimaryButton to true, so removing the label and the
                    // handler is not enough -- it renders an outlined button
                    // with no text in it. Raising a work order is the floating
                    // button in the module shell now.
                    showPrimaryButton={false}
                    tableColumns={columns}
                    tableData={records}
                    isLoading={isLoading}
                    pageNumber={page}
                    rowsPerPage={limit}
                    total={total}
                    onPageChange={setPage}
                    applySort={handleSort}
                    filters={filters}
                    selectedFilters={selectedFilters}
                    applyFilters={applyFilters}
                    applySearch={(selected, column) =>
                        // TableSearch hands back an ARRAY of chips; the list
                        // endpoint takes one `search` string, and an array
                        // serialises to search[0]=..&search[1]=.. which
                        // unknown = RAISE rejects.
                        applySearch(
                            Array.isArray(selected)
                                ? selected.filter(Boolean).join(",")
                                : selected,
                            column
                        )
                    }
                    searchableFields={searchableFields}
                    // Scoped to work orders and to the same column allowlist
                    // the search itself uses. The shared default fires the
                    // digital-twin device endpoint, which would offer battery
                    // IDs while the user types a reference id.
                    searchSuggestionsHandler={(term, columnName, suggestionLimit) =>
                        getWorkOrderSearchSuggestions(columnName, term, suggestionLimit)
                    }
                    disableSearch={!searchableFields.length}
                    initialColumns={pickableColumns}
                    selectedColumns={selectedColumns}
                    applySelectedColumns={setSelectedColumns}
                    defaultSelectedColumns={defaultSelectedColumns}
                    maxColumnsAllowed={pickableColumns.length}
                    columnOrder={columnOrder}
                    onColumnOrderChange={onColumnOrderChange}
                    getCSVData={getCSVData}
                    getCSVFileName={getCSVFileName}
                    onSendEmail={onSendEmail}
                    availableEmailDomains={emailDomains}
                    emailInfo={{
                        subject: "Work orders export",
                        // Explicit: EmailComposerDialog only copies its default
                        // body into the payload as a side effect of the user
                        // editing the subject, so an unedited send arrived with
                        // an empty body.
                        body: `Attached is the work order list exported from ${ASSET_MANAGEMENT_LABEL}.`,
                    }}
                    // No row selection on this table. The export endpoint
                    // returns rows WITHOUT an id (_serialiseExportRow), so a
                    // tick could not be matched to a downloaded row -- a
                    // selection-scoped file would either be empty or silently
                    // unscoped. Batch actions therefore always cover the whole
                    // filtered set, which is what alwaysEnableActions promises.
                    alwaysEnableActions
                    disablePagination={false}
                    emptyMessage={
                        errorMessage ||
                        (columns.length
                            ? activeTab === TAB_REQUESTS
                                ? "Nothing is waiting for a reviewer"
                                : "No reviewed work orders yet"
                            : "Columns are not configured for workOrdersTable")
                    }
                />
            </Box>

            <WorkOrderDetailView
                open={Boolean(detailTarget)}
                workOrder={detailTarget}
                onClose={() => setDetailTarget(null)}
                onReview={
                    reviewGateFor(detailTarget).allowed
                        ? (row) => {
                            setDetailTarget(null);
                            setReviewTarget(row);
                        }
                        : undefined
                }
                reviewBlockedReason={reviewGateFor(detailTarget).reason || ""}
                onEdit={canEditRow(detailTarget) ? openEdit : undefined}
                onWithdraw={
                    canEditRow(detailTarget)
                        ? (row) => setWithdrawTarget(row)
                        : undefined
                }
            />

            <ReviewWorkOrderDialog
                open={Boolean(reviewTarget)}
                workOrder={reviewTarget}
                currentUserID={userResolved ? currentUserID : undefined}
                onClose={() => setReviewTarget(null)}
                onReviewed={() => {
                    setReviewTarget(null);
                    // The row leaves Requests and appears in History, so both
                    // the list and the badges have to move.
                    refetch(records.length);
                }}
            />

            {/* Withdrawal confirm. Inline rather than its own component: it is
                one question with two buttons, and the destructive wording is
                the whole of it. */}
            <StyledDialog
                open={Boolean(withdrawTarget)}
                onClose={() => !withdrawing && setWithdrawTarget(null)}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography
                        variant="fw600s16c000000"
                        sx={{ fontSize: "20px", lineHeight: "24px", color: FORM_TOKENS.inkStrong }}
                    >
                        Withdraw this request?
                    </Typography>
                    <CloseButton onClick={() => !withdrawing && setWithdrawTarget(null)} />
                </Box>
                <Divider sx={{ my: 2.5, borderColor: FORM_TOKENS.fieldBorder }} />
                <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.inkBody }}>
                    {withdrawTarget?.workOrderReferenceID} will be withdrawn. Nothing
                    moves: a pending work order has never touched asset state, so there
                    is nothing to undo. The request itself is kept as a record of what
                    was asked for.
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
                    <CancelFormActionButton
                        data-testid="work-order-withdraw-cancel"
                        onClick={() => setWithdrawTarget(null)}
                        disabled={withdrawing}
                    >
                        Keep it
                    </CancelFormActionButton>
                    <PrimaryFormLoadingActionButton
                        data-testid="work-order-withdraw-confirm"
                        onClick={handleWithdraw}
                        loading={withdrawing}
                        disabled={withdrawing}
                    >
                        Withdraw
                    </PrimaryFormLoadingActionButton>
                </Box>
            </StyledDialog>
        </>
    );
};

export default WorkOrdersTabContent;
